import { useRef } from "react"
import Link from "next/link"
import Image from "next/image"
import dynamic from "next/dynamic"
import { useUserContext } from "context/UserContext"
import { useLang } from "hooks/useLang"
import { useRouter } from "next/dist/client/router"
import { FiMenu } from "react-icons/fi"
import useScrollPosition from "hooks/useScrollPosition"
import PassTicketsLogo from "components/icons/PassTicketsLogo"
import Sidebar from "components/bookingv2/Sidebar"
import Button from "components/Form/Button"
import { IoClose } from "react-icons/io5"
import CustomImage from "components/atoms/Image/CustomImage"
import SideBarItems from "components/bookingv2/SideBarItems"
import AppSocialMedia from "components/atoms/AppSocialMedia"

const PrivateNav = dynamic(() => import("./Navbar/PrivateNav"), { ssr: false })

interface IProps {
    isMenuOpen?: boolean
    setIsMenuOpen: (value: boolean) => void
    logo?: string
    orgId?: string
}

export const NAV_MAIN_LIST = [
    {
        title: "discover_title",
        description: "discover_description",
        linkTo: "/discover",
    },
    {
        title: "create_title",
        description: "create_description",
        linkTo: "/support/create-event",
    },
    {
        title: "contact_title",
        description: "contact_description",
        linkTo: "/support/contact-us",
    },
]

const NewNavbar = ({ isMenuOpen, setIsMenuOpen, logo, orgId }: IProps) => {
    const { user } = useUserContext()
    const { isAr, changeLanguage, t } = useLang()
    const { push } = useRouter()

    const scrollPosition = useScrollPosition()
    const isTop = scrollPosition === 0

    const passTicketsLogoColor = "light"
    const navItemColor = "text-white"

    const desktopMenuIconRef = useRef(null)
    const lang = isAr ? "mr-4" : "ml-4"

    const instagramUrl = `https://www.instagram.com/passtickets`
    const openInstagram = () => window.open(instagramUrl, "_blank")

    const router = useRouter()
    const isEventPage = router.asPath.includes("/events/")

    return (
        <>
            {/* NAVBAR */}
            <nav
                style={{ zIndex: 500 }}
                aria-label="Main Navigation"
                className={`fixed flex items-center justify-center w-full top-0 py-4 px-4 transform transition-colors duration-300 ease-out will-change-transform ${
                    isTop
                        ? ""
                        : "dark:bg-transparent  bg-opacity-50 shadow-3xl backdrop-blur-xl"
                }`}
            >
                <div className="flex h-full items-start md:items-center justify-between w-full max-w-[1180px] ">
                    <div
                        className="lg:flex-row flex-col flex justify-between lg:gap-[50px]"
                        aria-label="PassTickets"
                    >
                        <Link href={logo ? `/organizer/${orgId}` : "/"}>
                            {logo ? (
                                <CustomImage
                                    className="min-w-[5px]"
                                    alt="org"
                                    width={50}
                                    height={50}
                                    src={
                                        process.env.NEXT_PUBLIC_Event_Img_URL +
                                        logo
                                    }
                                />
                            ) : (
                                <PassTicketsLogo
                                    theme={passTicketsLogoColor}
                                    className="transition-transform duration-300 ease-out will-change-transform"
                                    height={35}
                                />
                            )}
                        </Link>

                        <div
                            className={`flex flex-row gap-4 mt-4 lg:mt-0 w-full lg:flex ${lang}`}
                            style={{ willChange: "transform, opacity" }}
                        >
                            {logo && (
                                <Link
                                    className="flex lg:flex flex-row items-center gap-1 hover:opacity-80 transition-opacity duration-300"
                                    href={"/"}
                                >
                                    <Image
                                        src="/svg/home.svg"
                                        alt="home"
                                        width={15}
                                        height={15}
                                        className="w-[15px] h-[15px] capitalize "
                                    />
                                    <span className="text-[14px] text-[#fff]">
                                        {t("home")}
                                    </span>
                                </Link>
                            )}
                            <Link
                                className="flex lg:flex flex-row items-center gap-1 hover:opacity-80 transition-opacity duration-300"
                                href="/discover"
                            >
                                <Image
                                    src="/svg/discover.svg"
                                    alt="discover"
                                    width={14}
                                    height={14}
                                    className="w-[14px] h-[14px]"
                                />
                                <span className="text-[14px] text-[#fff]">
                                    {t("discover")}
                                </span>
                            </Link>

                            {/* <Link
                className="lg:flex hidden flex-row items-center gap-1 hover:opacity-80 transition-opacity duration-300"
                href={"/events"}
              >
                <img src="/svg/location.svg" alt="location" className="w-[14px] h-[14px]" />
                <span className="text-[14px] text-[#fff] capitalize">{t("location")}</span>
              </Link> */}

                            <div className="flex flex-row gap-4 items-center">
                                {/* <Link
                  className="flex flex-row h-[25px] items-center gap-1 px-6 py-1 rounded-full hover:opacity-80 bg-[#19BF5E4D] border-[1px] border-[#19BF5E99] transition-colors duration-300"
                  href={"/"}
                >
                  <span className="text-[14px] text-[#fff] capitalize">National day</span>
                </Link> */}
                                {!isEventPage && (
                                    <Link
                                        className="flex flex-row h-[25px] items-center gap-1 px-4  rounded-full hover:opacity-80 bg-[#FF7B004D] border-[1px] border-[#B5580099] transition-colors duration-300"
                                        href={
                                            "https://pass-tickets.com/en-US/events/guvali"
                                        }
                                    >
                                        <span className="text-[14px] text-[#fff] capitalize">
                                            {t("guvali_season")}
                                        </span>
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* MOBILE MENU ICON */}
                    <FiMenu
                        aria-label="Mobile Menu Button"
                        onClick={() => setIsMenuOpen(true)}
                        className={`block mt-[-4px] md:mt-[0px] md:hidden cursor-pointer text-[40px] transition-transform duration-300 ease-out ${navItemColor}`}
                    />

                    {/* DESKTOP MENU */}
                    <div className="hidden md:flex gap-x-5 items-center">
                        {/* <Link className="flex flex-row items-center gap-1 hover:opacity-80 transition-opacity duration-300" href={"/"}>
              <img src="/svg/v2/search.svg" alt="search" className="w-[14px] h-[14px]" />
              <span className="text-[14px] text-[#fff]">{t("search")}</span>
            </Link> */}

                        <Link
                            className="flex flex-row items-center gap-1 hover:opacity-80 transition-opacity duration-300"
                            href={
                                isAr
                                    ? "https://business.pass-tickets.com/ar/host-better/"
                                    : "https://business.pass-tickets.com/host-better/"
                            }
                        >
                            <Image
                                src="/svg/v2/sell_with_us.svg"
                                alt="sell"
                                width={14}
                                height={14}
                                className="w-[14px] h-[14px]"
                            />
                            <span className="text-[14px] text-[#fff]">
                                {t("sell_with_us")}
                            </span>
                        </Link>

                        {user ? (
                            <PrivateNav allowColorTransition />
                        ) : (
                            <div className="flex flex-row ">
                                <div
                                    aria-label="Navbar Signup Button"
                                    onClick={() => {
                                        push("/user/login")
                                        setIsMenuOpen(false)
                                    }}
                                    className={`flex uppercase cursor-pointer rounded-full border px-4  text-[14px] transition-colors duration-300 bg-[#F28C28] hover:bg-[#d57c23] hover:border-[#d57c23] border-[#F28C28] ${navItemColor}`}
                                >
                                    {t("login")}
                                </div>
                            </div>
                        )}

                        {isAr ? (
                            <div onClick={() => changeLanguage()}>
                                <span className="text-[14px] cursor-pointer">
                                    {"EN"}
                                </span>
                            </div>
                        ) : (
                            <div onClick={() => changeLanguage()}>
                                <span className="text-[14px] cursor-pointer">
                                    {"AR"}
                                </span>
                            </div>
                        )}

                        <div ref={desktopMenuIconRef}>
                            <FiMenu
                                aria-label="Desktop Menu Button"
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className={`cursor-pointer text-2xl transition-transform duration-300 ease-out ${navItemColor}`}
                            />
                        </div>
                    </div>
                </div>
            </nav>

            {/* SIDEBAR DRAWER ABOVE EVERYTHING */}
            <Sidebar
                showDefaultHeader={false}
                isOpen={isMenuOpen || false}
                onClose={() => setIsMenuOpen(false)}
                className={`
    fixed top-0 z-[9999] w-full md:max-w-[450px] h-full 
    transform transition-transform duration-300 ease-in-out 
    bg-white dark:bg-white shadow-2xl
    ${isAr ? "right-0" : "right-0"}   // 👈 drawer position
  `}
                style={{
                    transform: isMenuOpen
                        ? "translateX(0)"
                        : isAr
                          ? "translateX(100%)" // 👈 Arabic pushes from the LEFT
                          : "translateX(100%)",
                    willChange: "transform",
                }}
            >
                <div className="all-sidebar-content flex flex-col h-full md:px-10 px-5">
                    {/* TOP SCROLLABLE */}
                    <div className="flex-1 overflow-y-auto flex flex-col gap-y-8 pb-10">
                        {/* HEADER */}
                        <div
                            className={`
          header flex items-start justify-between 
          ${isAr ? "flex-row-reverse" : "flex-row"}
        `}
                        >
                            {user ? (
                                <SideBarItems />
                            ) : (
                                <div
                                    aria-label="Navbar Login Button"
                                    onClick={() => {
                                        push("/user/login")
                                        setIsMenuOpen(false)
                                    }}
                                    className="flex cursor-pointer rounded-full border px-4 md:py-1 transition-colors duration-300 hover:border-gray-600 hover:text-gray-600 text-black border-black md:text-sm text-lg"
                                >
                                    {t("Sign_in")}
                                </div>
                            )}

                            <Button
                                className="text-white bg-[#BBBBBB] hover:bg-opacity-80 rounded-[8px] p-1"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                <IoClose className="text-2xl font-bold text-white" />
                            </Button>
                        </div>

                        {/* MAIN NAV ITEMS */}
                        <div
                            className={`
          main-nav-items flex flex-col gap-y-2
          ${isAr ? "items-start text-right" : "items-start text-left"}
        `}
                        >
                            {NAV_MAIN_LIST.map((item, index) => (
                                <Link
                                    className="cursor-pointer transition-colors duration-300 hover:text-gray-600 text-black flex flex-col"
                                    key={index}
                                    href={item.linkTo}
                                >
                                    <h3
                                        className={`capitalize md:text-m text-2xl text-[#303030] font-bold ${isAr ? "text-right" : "text-left"}`}
                                    >
                                        {t(item.title)}
                                    </h3>
                                    <span className="capitalize-first text-xs text-[#7C7C7C]">
                                        {t(item.description)}
                                    </span>
                                </Link>
                            ))}
                        </div>

                        {/* LANGUAGE SECTION */}
                        <div
                            className={`flex flex-col gap-y-2 mt-4 ${isAr ? "text-right" : "text-left"}`}
                        >
                            <h5 className="text-[#303030] font-bold capitalize md:text-m text-2xl">
                                {t("language")}
                            </h5>

                            <div
                                className={`
            flex gap-x-2 text-sm text-[#7C7C7C] items-center 
            ${isAr ? "flex-row-reverse" : "flex-row"}
          `}
                            >
                                <span
                                    className={`${isAr ? "text-[#F79E1BCC]" : "cursor-pointer"} md:text-m text-m`}
                                    onClick={
                                        isAr
                                            ? undefined
                                            : () => changeLanguage()
                                    }
                                >
                                    عربي
                                </span>

                                <span className="w-[5px] h-[5px] rounded-full bg-[#7C7C7C]"></span>

                                <span
                                    className={`${isAr ? "cursor-pointer" : "text-[#F79E1BCC]"} md:text-m text-m`}
                                    onClick={
                                        isAr
                                            ? () => changeLanguage()
                                            : undefined
                                    }
                                >
                                    English
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* BOTTOM SECTION */}
                    <div
                        className={`
        flex flex-col gap-y-10 pb-8
        ${isAr ? "text-left items-start" : "text-left items-start"}
      `}
                    >
                        {/* INSTAGRAM GRID */}
                        <div className="flex flex-col gap-y-3">
                            <h5
                                className={`text-black font-bold capitalize ${isAr ? "text-right" : "text-left"} md:text-m text-2xl `}
                            >
                                {t("follow_us")}
                            </h5>

                            <div
                                onClick={openInstagram}
                                className="flex flex-wrap gap-3 justify-start"
                            >
                                {[...Array(8)].map((_, index) => (
                                    <Image
                                        key={index}
                                        src={`/images/instagram/${index + 1}.png`}
                                        width={70}
                                        height={88}
                                        className="rounded-md object-cover w-[70px] cursor-pointer bg-[#BABABA]"
                                        style={{ aspectRatio: `300/375` }}
                                        alt={`instagram-${index}`}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* SOCIAL ICONS */}

                        <div className={`flex flex-col gap-y-3`}>
                            <h5 className="font-bold md:text-m text-2xl">
                                {t("social")}
                            </h5>
                            <AppSocialMedia />
                        </div>
                    </div>
                </div>
            </Sidebar>
        </>
    )
}

export default NewNavbar
